import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  approveAdminProjectDiscoveryContact,
  fetchAdminProjectDiscoveryApprovedContacts,
  fetchAdminProjectDiscoveryCandidates,
  fetchAdminProjectDiscoveryEvidence,
} from '../token/tokenIntelligenceApi';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 48px',
    maxWidth: 1240,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: { padding: '14px 12px 32px' },
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#9ea4d8',
    fontSize: 13,
    textDecoration: 'none',
    marginBottom: 18,
  },
  pageGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(320px, 420px) minmax(0, 1fr)',
    gap: 18,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  panel: {
    padding: '22px 24px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.16), rgba(10,14,30,0.96))',
  },
  title: {
    fontSize: '1.45rem',
    fontWeight: 900,
    color: '#e5e7ff',
    marginBottom: 8,
  },
  muted: {
    color: '#9ea4d8',
    fontSize: 13,
    lineHeight: 1.55,
  },
  body: {
    color: '#d5dcff',
    fontSize: 13,
    lineHeight: 1.55,
  },
  controls: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gap: 10,
    marginTop: 18,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  textField: {
    '& .MuiOutlinedInput-input': { color: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
    '& .MuiInputLabel-root': { color: '#9ea4d8' },
  },
  candidateCard: {
    marginTop: 12,
    padding: 14,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    cursor: 'pointer',
  },
  candidateCardActive: {
    borderColor: 'rgba(118,255,184,0.38)',
    background: 'rgba(118,255,184,0.08)',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  badge: {
    borderRadius: 999,
    border: '1px solid rgba(77,121,255,0.3)',
    background: 'rgba(77,121,255,0.12)',
    color: '#d5dcff',
    fontSize: 12,
    padding: '4px 10px',
  },
  section: {
    marginTop: 16,
    padding: 14,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
  },
  sectionLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 10,
  },
  evidenceItem: {
    padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  error: {
    color: '#ff9a9a',
    fontSize: 13,
    marginTop: 12,
  },
  success: {
    color: '#76ffb8',
    fontSize: 13,
    marginTop: 12,
  },
}));

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: n >= 100 ? 0 : 2 });
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

// Stored contacts already carry a real contactId, but telegram/x targets are
// synthesized client-side and the server derives their contactId as
// `contact_${sha256(`${type}:${value}`).slice(0,12)}` (see
// buildCandidateTargets in server/projectOutreachService.js). Mirroring that
// hash here is what lets the UI mark a social target "already approved"
// without an extra round trip through the (slow) evidence pipeline.
async function computeSocialContactId(type, value) {
  if (!value || !window.crypto || !window.crypto.subtle) return '';
  const text = `${type}:${value}`.toLowerCase();
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  const hex = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  return `contact_${hex.slice(0, 12)}`;
}

export default function ProjectDiscoveryReviewPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { candidateId } = useParams();
  const [statusFilter, setStatusFilter] = useState('review');
  const [minConfidence, setMinConfidence] = useState('0.6');
  const [listState, setListState] = useState({ loading: true, error: '', data: null });
  const [evidenceState, setEvidenceState] = useState({ loading: false, error: '', data: null });
  const [selectedContactKey, setSelectedContactKey] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [approvedContactIds, setApprovedContactIds] = useState([]);
  const [socialContactIdMap, setSocialContactIdMap] = useState({});
  const [forceRefreshing, setForceRefreshing] = useState(false);

  const candidateIdRef = useRef(candidateId);
  useEffect(() => {
    candidateIdRef.current = candidateId;
  }, [candidateId]);

  useEffect(() => {
    let active = true;
    setListState({ loading: true, error: '', data: null });
    fetchAdminProjectDiscoveryCandidates({
      status: statusFilter,
      minConfidence,
      limit: 50,
    }).then((result) => {
      if (!active) return;
      setListState({ loading: false, error: '', data: result });
      const candidates = result && Array.isArray(result.candidates) ? result.candidates : [];
      if (!candidateIdRef.current && candidates[0] && candidates[0].candidateId) {
        navigate(`/admin/project-discovery/${encodeURIComponent(candidates[0].candidateId)}`, { replace: true });
      }
    }).catch((error) => {
      if (!active) return;
      setListState({ loading: false, error: error && error.message ? error.message : 'Failed to load candidates', data: null });
    });
    return () => {
      active = false;
    };
  }, [minConfidence, navigate, statusFilter]);

  useEffect(() => {
    if (!candidateId) return undefined;
    let active = true;
    setEvidenceState({ loading: true, error: '', data: null });
    fetchAdminProjectDiscoveryEvidence(candidateId)
      .then((result) => {
        if (!active) return;
        setEvidenceState({ loading: false, error: '', data: result });
      })
      .catch((error) => {
        if (!active) return;
        setEvidenceState({ loading: false, error: error && error.message ? error.message : 'Failed to load evidence', data: null });
      });
    return () => {
      active = false;
    };
  }, [candidateId]);

  useEffect(() => {
    if (!candidateId) {
      setApprovedContactIds([]);
      return undefined;
    }
    let active = true;
    fetchAdminProjectDiscoveryApprovedContacts(candidateId)
      .then((result) => {
        if (!active) return;
        setApprovedContactIds(result && Array.isArray(result.approvedContactIds) ? result.approvedContactIds : []);
      })
      .catch(() => {
        if (active) setApprovedContactIds([]);
      });
    return () => {
      active = false;
    };
  }, [candidateId]);

  useEffect(() => {
    const socials = evidenceState.data && evidenceState.data.socials ? evidenceState.data.socials : {};
    if (!socials.telegram && !socials.x) {
      setSocialContactIdMap({});
      return undefined;
    }
    let active = true;
    Promise.all([
      socials.telegram ? computeSocialContactId('telegram', socials.telegram) : Promise.resolve(''),
      socials.x ? computeSocialContactId('x', socials.x) : Promise.resolve(''),
    ]).then(([telegram, x]) => {
      if (active) setSocialContactIdMap({ telegram, x });
    });
    return () => {
      active = false;
    };
  }, [evidenceState.data]);

  useEffect(() => {
    const contacts = evidenceState.data && Array.isArray(evidenceState.data.contacts) ? evidenceState.data.contacts : [];
    const socials = evidenceState.data && evidenceState.data.socials ? evidenceState.data.socials : {};
    const availableKeys = [
      ...contacts.map((item) => `contact:${item.contactId}`),
      ...(socials.telegram ? [`telegram:${socials.telegram}`] : []),
      ...(socials.x ? [`x:${socials.x}`] : []),
    ];
    if (availableKeys.length && !availableKeys.includes(selectedContactKey)) {
      setSelectedContactKey(availableKeys[0]);
    }
    if (!availableKeys.length) {
      setSelectedContactKey('');
    }
  }, [evidenceState.data, selectedContactKey]);

  const candidates = useMemo(() => (listState.data && Array.isArray(listState.data.candidates) ? listState.data.candidates : []), [listState.data]);
  const selected = evidenceState.data && evidenceState.data.candidate ? evidenceState.data.candidate : null;
  const scoring = evidenceState.data && evidenceState.data.scoring ? evidenceState.data.scoring : null;
  const approvalTargets = useMemo(() => {
    const contacts = evidenceState.data && Array.isArray(evidenceState.data.contacts) ? evidenceState.data.contacts : [];
    const socials = evidenceState.data && evidenceState.data.socials ? evidenceState.data.socials : {};
    // contactId is what's sent to the server (empty for socials — the server
    // resolves those by type+value instead). resolvedContactId is the id we
    // use locally to check the approved-contacts list, since socials only
    // get a real id once we hash them the same way the server does.
    const targets = contacts.map((item) => ({
      selectionKey: `contact:${item.contactId}`,
      contactId: item.contactId,
      resolvedContactId: item.contactId,
      contactType: item.type,
      contactValue: item.value || '',
      label: `${item.type}: ${item.value || 'stored contact'}`,
    }));
    if (socials.telegram) {
      targets.push({ selectionKey: `telegram:${socials.telegram}`, contactId: '', resolvedContactId: socialContactIdMap.telegram || '', contactType: 'telegram', contactValue: socials.telegram, label: `telegram: ${socials.telegram}` });
    }
    if (socials.x) {
      targets.push({ selectionKey: `x:${socials.x}`, contactId: '', resolvedContactId: socialContactIdMap.x || '', contactType: 'x', contactValue: socials.x, label: `x: ${socials.x}` });
    }
    return targets;
  }, [evidenceState.data, socialContactIdMap]);

  const selectedApprovalTarget = approvalTargets.find((item) => item.selectionKey === selectedContactKey) || approvalTargets[0] || null;
  const selectedTargetApproved = !!(selectedApprovalTarget && selectedApprovalTarget.resolvedContactId && approvedContactIds.includes(selectedApprovalTarget.resolvedContactId));

  const handleForceRefresh = () => {
    if (!candidateId) return;
    setForceRefreshing(true);
    fetchAdminProjectDiscoveryEvidence(candidateId, { forceRefresh: true })
      .then((result) => {
        setEvidenceState({ loading: false, error: '', data: result });
      })
      .catch((error) => {
        setEvidenceState((prev) => ({ ...prev, error: error && error.message ? error.message : 'Failed to refresh evidence' }));
      })
      .finally(() => setForceRefreshing(false));
  };

  const handleApproveContact = async () => {
    if (!candidateId || !selectedApprovalTarget || selectedTargetApproved) return;
    setApprovalSaving(true);
    setApprovalError('');
    setApprovalStatus('');
    try {
      const result = await approveAdminProjectDiscoveryContact(candidateId, {
        contactId: selectedApprovalTarget.contactId,
        contactType: selectedApprovalTarget.contactType,
        contactValue: selectedApprovalTarget.contactValue,
        reason: approvalReason,
      });
      const resolvedId = (result && result.contact && result.contact.contactId) || selectedApprovalTarget.resolvedContactId;
      if (resolvedId) {
        setApprovedContactIds((prev) => (prev.includes(resolvedId) ? prev : [...prev, resolvedId]));
      }
      if (result && result.alreadyApproved) {
        setApprovalStatus('This contact was already approved earlier — no new approval was recorded.');
      } else {
        setApprovalStatus(result && result.blocked
          ? 'Contact approved and recorded. Actual sending stays off regardless — it requires PROJECT_OUTREACH_ENABLED=1 on the server, a separate infrastructure/legal switch, not a per-contact setting.'
          : 'Contact approved for outreach review.');
      }
    } catch (error) {
      setApprovalError(error && error.message ? error.message : 'Failed to approve contact');
    } finally {
      setApprovalSaving(false);
    }
  };

  return (
    <Box className={classes.root}>
      <RouterLink to={selected && selected.tokenAddress ? `/token/${selected.tokenAddress}` : '/token'} className={classes.backLink}>
        ← Back to token
      </RouterLink>

      <Box className={classes.pageGrid}>
        <Box className={classes.panel}>
          <Typography className={classes.title}>Project Discovery Review</Typography>
          <Typography className={classes.muted}>
            Discovery evidence with manual outreach approval controls. Actual send paths remain gated until provider, legal, and scope decisions are configured.
          </Typography>

          <Box className={classes.controls}>
            <TextField
              select
              variant="outlined"
              label="Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className={classes.textField}
            >
              <MenuItem value="review">Review</MenuItem>
              <MenuItem value="queued">Queued</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
            <TextField
              variant="outlined"
              label="Min confidence"
              value={minConfidence}
              onChange={(event) => setMinConfidence(event.target.value)}
              className={classes.textField}
            />
            <Button variant="outlined" onClick={() => window.location.reload()} style={{ color: '#cfd6ff', borderColor: 'rgba(255,255,255,0.24)' }}>
              Refresh
            </Button>
          </Box>

          {listState.loading ? <Typography className={classes.body} style={{ marginTop: 18 }}>Loading candidates…</Typography> : null}
          {listState.error ? <Typography className={classes.error}>{listState.error}</Typography> : null}

          {candidates.map((candidate) => {
            const active = candidateId === candidate.candidateId;
            return (
              <Box
                key={candidate.candidateId}
                className={`${classes.candidateCard} ${active ? classes.candidateCardActive : ''}`}
                onClick={() => navigate(`/admin/project-discovery/${encodeURIComponent(candidate.candidateId)}`)}
              >
                <Typography className={classes.body} style={{ fontWeight: 700 }}>
                  {candidate.name || 'Unnamed token'} {candidate.symbol ? `(${candidate.symbol})` : ''}
                </Typography>
                <Typography className={classes.muted}>{candidate.tokenAddress}</Typography>
                <Box className={classes.badgeRow}>
                  <Box className={classes.badge}>{candidate.reviewStatus}</Box>
                  <Box className={classes.badge}>Website {formatNumber(candidate.websiteConfidence)}</Box>
                  <Box className={classes.badge}>Contact {formatNumber(candidate.contactConfidence)}</Box>
                  <Box className={classes.badge}>Score {formatNumber(candidate.outreachEligibilityScore)}</Box>
                </Box>
                <Typography className={classes.muted} style={{ marginTop: 10 }}>
                  Liquidity ${formatNumber(candidate.activity && candidate.activity.liquidityUsd)} · Holders {formatNumber(candidate.activity && candidate.activity.holderCount)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box className={classes.panel}>
          <Typography className={classes.title}>Evidence</Typography>
          <Typography className={classes.muted}>
            Candidate evidence, live owner and deployer enrichment, and scoring explanation.
          </Typography>

          {evidenceState.loading ? <Typography className={classes.body} style={{ marginTop: 18 }}>Loading evidence…</Typography> : null}
          {evidenceState.error ? <Typography className={classes.error}>{evidenceState.error}</Typography> : null}

          {!evidenceState.loading && selected ? (
            <>
              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Candidate</Typography>
                <Typography className={classes.body} style={{ fontWeight: 700 }}>
                  {selected.name || 'Unnamed token'} {selected.symbol ? `(${selected.symbol})` : ''}
                </Typography>
                <Typography className={classes.muted}>{selected.tokenAddress}</Typography>
                <Box className={classes.badgeRow}>
                  <Box className={classes.badge}>{selected.reviewStatus}</Box>
                  <Box className={classes.badge}>Liquidity ${formatNumber(selected.activity && selected.activity.liquidityUsd)}</Box>
                  <Box className={classes.badge}>Swap 30d ${formatNumber(selected.activity && selected.activity.swapVolumeUsd30d)}</Box>
                  <Box className={classes.badge}>Transfers 30d {formatNumber(selected.activity && selected.activity.transferCount30d)}</Box>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                  <Typography className={classes.muted}>
                    {evidenceState.data.lastScannedAt ? `Last scanned ${formatDate(evidenceState.data.lastScannedAt)}` : 'Last scanned unknown'}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleForceRefresh}
                    disabled={forceRefreshing}
                    style={{ color: '#cfd6ff', borderColor: 'rgba(255,255,255,0.24)' }}
                  >
                    {forceRefreshing ? 'Refreshing…' : 'Force refresh'}
                  </Button>
                </Box>
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Scoring</Typography>
                {scoring ? (
                  <>
                    <Typography className={classes.body}>Website confidence: <strong>{formatNumber(scoring.websiteConfidence)}</strong></Typography>
                    <Typography className={classes.body}>Contact confidence: <strong>{formatNumber(scoring.contactConfidence)}</strong></Typography>
                    <Typography className={classes.body}>Wallet identity confidence: <strong>{formatNumber(scoring.walletIdentityConfidence)}</strong></Typography>
                    <Typography className={classes.body}>Verification risk score: <strong>{formatNumber(scoring.verificationRiskScore)}</strong></Typography>
                    <Typography className={classes.body}>Outreach eligibility score: <strong>{formatNumber(scoring.outreachEligibilityScore)}</strong></Typography>
                  </>
                ) : null}
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Live Checks</Typography>
                <Typography className={classes.body}>Owner: {evidenceState.data && evidenceState.data.liveChecks ? evidenceState.data.liveChecks.ownerAddress || 'unknown' : 'unknown'}</Typography>
                <Typography className={classes.body}>Deployer: {evidenceState.data && evidenceState.data.liveChecks ? evidenceState.data.liveChecks.deployerAddress || 'unknown' : 'unknown'}</Typography>
                <Typography className={classes.body}>
                  ENS: {evidenceState.data && evidenceState.data.liveChecks && Array.isArray(evidenceState.data.liveChecks.ensNames) && evidenceState.data.liveChecks.ensNames.length
                    ? evidenceState.data.liveChecks.ensNames.map((item) => item.ensName).join(', ')
                    : 'none'}
                </Typography>
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Contacts</Typography>
                {(evidenceState.data.contacts || []).length ? (evidenceState.data.contacts || []).map((item) => (
                  <Box key={item.contactId} className={classes.evidenceItem}>
                    <Typography className={classes.body}>
                      {item.type}: {item.value || 'stored contact'}
                      {approvedContactIds.includes(item.contactId) ? (
                        <Box component="span" className={classes.badge} style={{ marginLeft: 8, borderColor: 'rgba(118,255,184,0.4)', background: 'rgba(118,255,184,0.12)', color: '#76ffb8' }}>Approved</Box>
                      ) : null}
                    </Typography>
                    <Typography className={classes.muted}>{item.sourceUrl || 'No source URL'} · confidence {formatNumber(item.confidenceScore)}</Typography>
                  </Box>
                )) : <Typography className={classes.muted}>No public contacts discovered.</Typography>}
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Outreach Approval</Typography>
                <Typography className={classes.muted}>
                  Approve one candidate contact at a time. Suppressed contacts and blocked domains are rejected server-side.
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="Target"
                  value={selectedContactKey}
                  onChange={(event) => setSelectedContactKey(event.target.value)}
                  className={classes.textField}
                  style={{ marginTop: 14 }}
                >
                  {approvalTargets.length ? approvalTargets.map((item) => {
                    const approved = !!(item.resolvedContactId && approvedContactIds.includes(item.resolvedContactId));
                    return (
                      <MenuItem key={item.selectionKey} value={item.selectionKey}>
                        {item.label}{approved ? ' · Approved' : ''}
                      </MenuItem>
                    );
                  }) : <MenuItem value="" disabled>No outreach targets available</MenuItem>}
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  label="Approval reason"
                  value={approvalReason}
                  onChange={(event) => setApprovalReason(event.target.value)}
                  className={classes.textField}
                  helperText="Stored on the approval record for auditability."
                />
                <Box style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                    style={{ color: '#cfd6ff', borderColor: 'rgba(255,255,255,0.24)' }}
                    disabled={approvalSaving}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApproveContact}
                    disabled={approvalSaving || !selectedApprovalTarget || selectedTargetApproved}
                    startIcon={approvalSaving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : null}
                    style={{ background: selectedTargetApproved ? '#2f8f5b' : '#4d79ff', color: '#fff', fontWeight: 700 }}
                  >
                    {approvalSaving ? 'Approving…' : selectedTargetApproved ? 'Approved ✓' : 'Approve Contact'}
                  </Button>
                </Box>
                {approvalError ? <Typography className={classes.error}>{approvalError}</Typography> : null}
                {approvalStatus ? <Typography className={classes.success}>{approvalStatus}</Typography> : null}
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Website Candidates</Typography>
                {(evidenceState.data.websiteCandidates || []).length ? (evidenceState.data.websiteCandidates || []).map((item) => (
                  <Box key={item.candidateId} className={classes.evidenceItem}>
                    <Typography className={classes.body}>{item.url}</Typography>
                    <Typography className={classes.muted}>{item.domain} · confidence {formatNumber(item.confidenceScore)} · {item.status}</Typography>
                  </Box>
                )) : <Typography className={classes.muted}>No website candidates.</Typography>}
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Evidence Log</Typography>
                {(evidenceState.data.evidence || []).length ? (evidenceState.data.evidence || []).map((item) => (
                  <Box key={item.evidenceId} className={classes.evidenceItem}>
                    <Typography className={classes.body}>{item.predicate} · {item.sourceType}</Typography>
                    <Typography className={classes.muted}>{item.sourceUrl || 'No source URL'}</Typography>
                    <Typography className={classes.body}>{item.supportingExcerpt || 'No excerpt stored.'}</Typography>
                    <Typography className={classes.muted}>Confidence {formatNumber(item.confidenceScore)} · {formatDate(item.retrievedAt || item.lastVerifiedAt)}</Typography>
                  </Box>
                )) : <Typography className={classes.muted}>No evidence rows.</Typography>}
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Fraud Flags</Typography>
                {(evidenceState.data.fraudFlags || []).length ? (
                  <Box className={classes.badgeRow}>
                    {(evidenceState.data.fraudFlags || []).map((flag) => <Box key={flag} className={classes.badge}>{flag}</Box>)}
                  </Box>
                ) : <Typography className={classes.muted}>No security exclusions detected.</Typography>}
              </Box>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
